import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '~/lib/query-client';
import { initMetaPixel } from '~/lib/meta-pixel';
import { captureFirstTouchAttribution, captureLandingPrompt } from '~/lib/attribution';
import { installChunkReloadGuard } from '~/lib/chunk-reload';
import App from './App';

import '@unocss/reset/tailwind-compat.css';
import 'react-toastify/dist/ReactToastify.css';
import '@xterm/xterm/css/xterm.css';
import './styles/index.scss';
import 'virtual:uno.css';

// A tab left open across a deploy requests chunk names that no longer exist;
// reload it once onto the new build instead of breaking the screen.
installChunkReloadGuard();

// Before render: conversion.ts calls fbq() at signup and needs the queue to exist.
initMetaPixel();

/*
 * Also before render, and this ordering is load-bearing.
 *
 * These used to run in App's mount effect. Effects fire child-first, parent
 * last, so ChatImpl's `useState(() => getDraft(draftId))` initializer ran
 * BEFORE App's effect had written the draft, and read an empty string. Because
 * ChatImpl is memoised and nothing remounts it on `/` (login is an inline
 * modal, no navigation, no reload), the seeded prompt was never picked up on
 * the visit that carried it. A visitor typed their idea into the marketing
 * hero, pressed Build, and landed on an empty composer.
 *
 * Running them here means the draft exists before the first render, so the
 * mount-time read finds it.
 *
 * captureFirstTouchAttribution MUST stay first: captureLandingPrompt strips
 * `?prompt=` from the URL, and attribution needs to read it for initial_prompt.
 */
captureFirstTouchAttribution();
captureLandingPrompt();

createRoot(document.getElementById('root')!).render(
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </BrowserRouter>,
);
